// Contextual Panel Component
// Date: December 4, 2025
// Purpose: Side panel for showing related data and actions in detail pages

import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';

export interface ContextualPanelItem {
  id: string;
  title: string;
  description?: string;
  /** Optional key-value pairs shown below description (e.g. Position, Function, Business, Geo) */
  details?: Record<string, string>;
  status?: string;
  metadata?: Record<string, any>;
  onClick?: () => void;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
}

export interface ContextualPanelProps {
  title: string;
  items: ContextualPanelItem[];
  isLoading?: boolean;
  emptyMessage?: string;
  onCreateNew?: () => void;
  onCreateLabel?: string;
  onViewAll?: () => void;
  onViewAllLabel?: string;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  renderCreateForm?: () => React.ReactNode;
  showCreateForm?: boolean;
  onCancelCreate?: () => void;
  customContent?: React.ReactNode;
  /** Optional class for the wrapper div around customContent (e.g. reduce top spacing) */
  customContentWrapperClassName?: string;
  /** When true, item rows do not show hover background */
  disableItemHover?: boolean;
  /** When true, hide the header bar (title + top border); show only a small close button */
  hideHeader?: boolean;
}

export const ContextualPanel: React.FC<ContextualPanelProps> = ({
  title,
  items,
  isLoading = false,
  emptyMessage = 'No items found',
  onCreateNew,
  onCreateLabel = 'Create New',
  onViewAll,
  onViewAllLabel = 'View All',
  isOpen,
  onClose,
  className = '',
  renderCreateForm,
  showCreateForm = false,
  onCancelCreate,
  customContent,
  customContentWrapperClassName,
  disableItemHover = false,
  hideHeader = false
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay - below navbar so top bar stays visible */}
      <div
        className="fixed top-14 right-0 bottom-0 left-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel - starts below navbar; flex column so content area can scroll */}
      <div
        className={`
          fixed right-0 top-14 bottom-0 w-full max-w-md bg-[var(--color-secondary-bg)] 
          shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col min-h-0
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          ${className}
        `}
      >
        {hideHeader ? (
          /* Minimal close only, no top line */
          <div className="flex justify-end shrink-0 pt-2 pr-2">
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-[var(--color-hover-bg)] text-[var(--color-secondary-text)]"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          /* Header: compact so content has more room */
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--color-border)] shrink-0">
            <h2 className="text-base font-semibold text-[var(--color-surface-text)]">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-[var(--color-hover-bg)] text-[var(--color-secondary-text)]"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Actions Bar */}
        {(onCreateNew || onViewAll) && !showCreateForm && (
          <div className="flex items-center gap-2 p-4 border-b border-[var(--color-border)] shrink-0">
            {onCreateNew && (
              <Button
                variant="primary"
                size="sm"
                onClick={onCreateNew}
                className="flex-1"
              >
                {onCreateLabel}
              </Button>
            )}
            {onViewAll && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onViewAll}
                className="flex-1"
              >
                {onViewAllLabel}
              </Button>
            )}
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && renderCreateForm && (
          <div className="p-4 border-b border-[var(--color-border)] shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-[var(--color-surface-text)]">Create New</h3>
              {onCancelCreate && (
                <button
                  onClick={onCancelCreate}
                  className="text-xs text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]"
                >
                  Cancel
                </button>
              )}
            </div>
            {renderCreateForm()}
          </div>
        )}

        {/* Content: flex-1 + min-h-0 so this area scrolls when content overflows */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 pt-0">
          {isLoading && !customContent ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
            </div>
          ) : items.length === 0 && !customContent ? (
            <div className="text-center py-8">
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75">{emptyMessage}</p>
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`
                    p-4 rounded-lg border border-[var(--color-border)] 
                    bg-[var(--color-primary-bg)]
                    transition-colors
                    ${item.onClick ? 'cursor-pointer' : ''}
                    ${!disableItemHover ? 'hover:bg-[var(--color-hover-bg)]' : ''}
                  `}
                  onClick={item.onClick}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-[var(--color-surface-text)] mb-1">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-xs text-[var(--color-secondary-text)] opacity-75 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                      {item.details && Object.keys(item.details).length > 0 && (
                        <dl className="mt-2 space-y-0.5 text-xs text-[var(--color-secondary-text)]">
                          {Object.entries(item.details).map(([label, value]) =>
                            value ? (
                              <div key={label} className="flex gap-2">
                                <dt className="font-medium text-[var(--color-surface-text)] shrink-0">{label}:</dt>
                                <dd className="opacity-90">{value}</dd>
                              </div>
                            ) : null
                          )}
                        </dl>
                      )}
                      {item.status && (
                        <span className="inline-block mt-2 px-2 py-1 text-xs rounded bg-[var(--color-accent-bg)] bg-opacity-20 text-[var(--color-accent-text)]">
                          {item.status}
                        </span>
                      )}
                    </div>
                    {item.actions && item.actions.length > 0 && (
                      <div className="flex items-center gap-1 ml-2">
                        {item.actions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              action.onClick();
                            }}
                            className={`
                              px-2 py-1 text-xs rounded transition-colors
                              ${action.variant === 'danger'
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : action.variant === 'primary'
                                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:opacity-90'
                                : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] hover:bg-[var(--color-hover-bg)]'
                              }
                            `}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {/* Custom Content */}
          {customContent && (
            <div className={customContentWrapperClassName ?? 'mt-6 border-t border-[var(--color-border)] pt-6'}>
              {customContent}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

